import { InputActions, type Layer, ObjectCategory, ZIndexes } from "@common/constants";
import { type OctoArmData, OctoArmState, type OctoData, OctoRig } from "@common/definitions/octo";
import { CircleHitbox } from "@common/utils/hitbox";
import { adjacentOrEqualLayer, adjacentOrEquivLayer } from "@common/utils/layer";
import { Numeric } from "@common/utils/math";
import { Vec, type Vector } from "@common/utils/vector";
import { Container, Sprite, Texture } from "pixi.js";
import { Game } from "../game";
import { CameraManager } from "../managers/cameraManager";
import { InputManager } from "../managers/inputManager";
import { ParticleManager } from "../managers/particleManager";
import { type GameSound, SoundManager } from "../managers/soundManager";
import { type Player } from "../objects/player";
import { PIXI_SCALE } from "../utils/constants";
import { toPixiCoords, TrenchioSprite } from "../utils/pixi";

/*
    The Octo Gadget's arms in the browser (1.10; what they do is in common/src/definitions/octo.ts, the rules on the
    server, server/src/octo/octoSystem.ts). Whoever carries the gadget wears its harness on their back (drawn in their
    own container, under the body, peeking out behind it) and four arms of linked steel vertebrae with a three-pronged
    claw at each end, drawn here from what the server sends with the player (`OctoData`: each arm's state, and where
    its claw is):
     - ready, an arm rests curled round its player, alive: it sways on its own slow clock, its claw turning a little as
       if looking about, its red lens pulsing, and it trails a touch behind as its player turns (the owner: "like its
       alive, the arms, like it has its own brain");
     - flying, it whips out straight to where the server says its claw is, the links strung along a line that sags
       and shivers as it goes;
     - pulling, it holds taut from the harness to its hold, shivering under the load, its claw closed, while its
       player is dragged along it, dust kicked up behind their boots and the winch heard (no wind streaks: the owner
       didn't like those);
     - coming back, it slackens into a wave and reels in, the claw last.
    With sounds: the shot, the clamp, a strike, the winch's reel, the servo's return (tools/sfx/octo_sounds.py).

    1.10.1's audit: a flying claw is reckoned on between the server's words (`_flyingClaw`: smooth, not in the ticks'
    steps, stopped where the server would stop it); the links are strung at a fixed spacing from the wrist, paid out
    from under the body (they slid along the chain whenever their count changed); the arms are drawn in whatever
    container the body is in (a floor fading in left them behind); the sparks and the dust move (their speeds were
    written as if a millisecond's); the shot is heard even when the claw strikes or clamps within the first tick; the
    winch is heard for the pull and no longer.
*/

/** a link along an arm every this many pixels at size 1 (a link's picture is 40 long, drawn at LINK_SCALE: they overlap, a chain) */
const LINK_SPACING = 24;
/**
 * the links' and the claw's pictures, scaled (1.10.2: a size down, the arms a little slimmer than a third of the body
 * across; the owner, of the arms at rest: "look weird and too long i guess ? ... maybe those should be for smaller
 * distances, and smaller")
 */
const LINK_SCALE = 1.1;
const CLAW_SCALE = 0.95;
/** the claw's prongs reach this far past its wrist (pixels at size 1), its lens this far */
const CLAW_REACH = 56 * CLAW_SCALE;
const LENS = 22.6;
/** the most links an arm is ever drawn with (its reach, and a little slack) */
const MAX_LINKS = 52;
/** the harness on the back: this far behind the player's middle (pixels at size 1) */
const HARNESS_BACK = 31;
/** the lens's glow (pixels): a soft bloom round the lens, not a blob over it (1.10.2) */
const GLOW = 24;
/** how quickly a drawn claw follows where it should be (ms): resting, flying, pulling, coming back */
const FOLLOW_MS: Record<OctoArmState, number> = { [OctoArmState.Ready]: 140, [OctoArmState.Flying]: 22, [OctoArmState.Pulling]: 30, [OctoArmState.Returning]: 60 };

/**
 * an arm's resting pose, in its player's own frame (units, facing +x): where its claw rests, and the two bends of its
 * curve from the harness. Compact (1.10.2): the high ones curl round the body's sides, their claws hovering by the
 * shoulders and looking ahead; the low ones angle back in a V, short, their claws looking out behind (1.10's reared up
 * past the shoulders and sprawled out behind, seven units and more: the owner, "the back octo hands just look weird
 * and too long")
 */
interface Pose {
    readonly tip: Vector
    readonly bend1: Vector
    readonly bend2: Vector
    /** the claw's rest heading, from the arm's line (radians) */
    readonly turn: number
}
const POSES: readonly Pose[] = [
    { tip: Vec(1.4, -4.3), bend1: Vec(-2.8, -3.4), bend2: Vec(-0.6, -5), turn: 0.45 },
    { tip: Vec(1.4, 4.3), bend1: Vec(-2.8, 3.4), bend2: Vec(-0.6, 5), turn: -0.45 },
    { tip: Vec(-4.4, -3.4), bend1: Vec(-3.6, -0.9), bend2: Vec(-4.6, -2), turn: -0.92 },
    { tip: Vec(-4.4, 3.4), bend1: Vec(-3.6, 0.9), bend2: Vec(-4.6, 2), turn: 0.92 }
];

interface ArmView {
    readonly index: number
    readonly container: Container
    readonly links: TrenchioSprite[]
    readonly claw: TrenchioSprite
    readonly glow: Sprite
    state: OctoArmState
    /** where the claw's wrist is drawn (pixels, the world's), easing toward where it should be */
    tip: Vector
    /** resting: the wrist in its player's own frame (units), easing toward the pose (it moves with the body, lags the turn) */
    localTip: Vector
    wasResting: boolean
    /** pulling: the hold, fixed in the world as it was first told (the player moves, the hold doesn't) */
    hold?: Vector
    /** when the state last changed, and when the arm's data last came (performance.now()) */
    changedAt: number
    dataAt: number
    /** pulling: the winch, heard while it pulls */
    reel?: GameSound
    /** its own slow clock's offset, so no two arms move together */
    readonly phase: number
}

interface RigView {
    readonly harness: TrenchioSprite
    readonly arms: ArmView[]
    layer: Layer
    /** the last dust kicked up by a pull */
    dustAt: number
}

/** a soft round glow, made once */
let glowTexture: Texture | undefined;
const glowOf = (): Texture => {
    if (glowTexture) return glowTexture;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 96;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        // (hot and small at the lens, fading fast: the lens's own picture does the looks, this is its light on the air)
        const gradient = ctx.createRadialGradient(48, 48, 0, 48, 48, 48);
        gradient.addColorStop(0, "rgba(255, 90, 60, 0.85)");
        gradient.addColorStop(0.18, "rgba(255, 50, 35, 0.4)");
        gradient.addColorStop(0.5, "rgba(255, 30, 20, 0.1)");
        gradient.addColorStop(1, "rgba(255, 20, 15, 0)");
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 96, 96);
    }
    glowTexture = Texture.from(canvas);
    return glowTexture;
};

/** a point of a cubic curve */
const cubic = (p0: Vector, p1: Vector, p2: Vector, p3: Vector, t: number): Vector => {
    const u = 1 - t;
    const a = u * u * u, b = 3 * u * u * t, c = 3 * u * t * t, d = t * t * t;
    return Vec(a * p0.x + b * p1.x + c * p2.x + d * p3.x, a * p0.y + b * p1.y + c * p2.y + d * p3.y);
};

class OctoFxClass {
    private readonly _rigs = new Map<Player, RigView>();
    private _time = 0;

    /** the arms as the server last sent them with `player` (undefined: no gadget; their view goes) */
    setArms(player: Player, data: OctoData | undefined, isNew: boolean): void {
        if (data === undefined) {
            this.forget(player);
            return;
        }
        let rig = this._rigs.get(player);
        let fresh = isNew;
        if (!rig) {
            // (the gadget just taken up: whatever its arms are doing is found, not heard)
            rig = this._makeRig(player);
            fresh = true;
        }
        const now = performance.now();
        data.forEach((arm, index) => {
            const view = rig.arms[index];
            if (!view) return;
            this._place(player, view, arm, fresh, now);
        });
    }

    /** `player`'s rig gone (the gadget dropped, the player gone) */
    forget(player: Player): void {
        const rig = this._rigs.get(player);
        if (!rig) return;
        this._rigs.delete(player);
        rig.harness.destroy();
        for (const arm of rig.arms) {
            arm.reel?.stop();
            arm.container.destroy({ children: true });
        }
    }

    /** the local player shoots an arm (the octo key, console `octo`): the server decides whether one goes */
    fire(): void {
        const me = Game.activePlayer;
        if (!me || Game.spectating || me.dead || me.octoArms === undefined) return;
        InputManager.addAction(InputActions.OctoArm);
    }

    reset(): void {
        for (const player of [...this._rigs.keys()]) this.forget(player);
    }

    private _makeRig(player: Player): RigView {
        const harness = new TrenchioSprite("octo_harness");
        harness.setPos(-HARNESS_BACK, 0).setZIndex(-0.9);
        player.container.addChild(harness);
        const arms: ArmView[] = [];
        for (let index = 0; index < OctoRig.arms; index++) {
            const container = new Container();
            container.zIndex = ZIndexes.Players - 0.3;
            const claw = new TrenchioSprite("octo_claw");
            claw.anchor.set(0.1, 0.5);
            const glow = new Sprite(glowOf());
            glow.anchor.set(0.5);
            glow.blendMode = "add";
            glow.width = glow.height = GLOW;
            container.addChild(claw, glow);
            const pose = POSES[index] ?? POSES[0];
            arms.push({
                index, container, links: [], claw, glow, state: OctoArmState.Ready,
                tip: Vec(player.container.position.x, player.container.position.y), localTip: Vec.clone(pose.tip), wasResting: true,
                changedAt: performance.now(), dataAt: performance.now(), phase: (player.id * 1.7 + index * 2.3) % (Math.PI * 2)
            });
        }
        const rig: RigView = { harness, arms, layer: player.layer, dustAt: 0 };
        this._rigs.set(player, rig);
        this._mount(player, rig);
        return rig;
    }

    /**
     * the arms' containers in the container the body is drawn in (the camera's for the player's floor, which changes
     * on the stairs, or the one a floor fades in through: wherever the body goes, the arms go)
     */
    private _mount(player: Player, rig: RigView): void {
        // (a player just made has no floor yet: their floor comes with their full data, and the arms are mounted then)
        if (player.layer === undefined) return;
        const target = player.container.parent ?? CameraManager.getContainer(player.layer);
        if (!target) return;
        rig.layer = player.layer;
        for (const arm of rig.arms) if (arm.container.parent !== target) target.addChild(arm.container);
    }

    /** an arm's state as the server says it: the sounds and sparks of a change */
    private _place(player: Player, view: ArmView, arm: OctoArmData, isNew: boolean, now: number): void {
        const was = view.state;
        view.state = arm.state;
        view.dataAt = now;
        if (arm.state === OctoArmState.Pulling) {
            // (the hold, where it was first told: the player's own position lags a tick behind it meanwhile)
            if (was !== OctoArmState.Pulling || !view.hold) view.hold = toPixiCoords(this._clawOf(player, arm, player.position));
        } else if (arm.state !== OctoArmState.Returning) {
            view.hold = undefined;
        }
        if (was === arm.state) return;
        view.changedAt = now;
        if (was === OctoArmState.Pulling) {
            view.reel?.stop();
            view.reel = undefined;
        }
        if (isNew) return;
        const at = this._clawOf(player, arm, player.position);
        // (a shot whose claw strikes or clamps within the first tick is told clamped or coming back straight from ready)
        if (was === OctoArmState.Ready && arm.state !== OctoArmState.Ready) player.playSound("octo_shoot", { falloff: 0.5, maxRange: 96 });
        switch (arm.state) {
            case OctoArmState.Pulling:
                SoundManager.play("octo_clamp", { position: at, layer: player.layer, falloff: 0.45, maxRange: 110 });
                this._sparks(at, player.layer, 6, 0xffffff);
                view.reel = player.playSound("octo_reel", { falloff: 0.5, maxRange: 80, loop: true });
                break;
            case OctoArmState.Returning:
                if (was !== OctoArmState.Pulling) {
                    // (a strike: it came back without clamping on)
                    SoundManager.play("octo_hit", { position: at, layer: player.layer, falloff: 0.45, maxRange: 110 });
                    this._sparks(at, player.layer, 8, 0xff7a60);
                } else {
                    player.playSound("octo_return", { falloff: 0.5, maxRange: 80 });
                }
                break;
            case OctoArmState.Flying:
            case OctoArmState.Ready:
                break;
        }
    }

    /** where an arm's claw is in the world (units), from its data: from the body as it's drawn, or as the server has it */
    private _clawOf(player: Player, arm: OctoArmData, from: Vector = player.drawnPosition): Vector {
        return Vec.add(from, Vec.fromPolar(arm.angle, arm.distance));
    }

    /**
     * a flying claw reckoned on from the server's last word at the arm's speed (the whip drawn smooth between the
     * server's ticks, not in their steps), stopped short of anything solid or anyone in its way as the server stops it
     */
    private _flyingClaw(player: Player, arm: OctoArmData, elapsed: number): Vector {
        const direction = Vec.fromPolar(arm.angle);
        const from = player.drawnPosition;
        const ahead = Math.min(OctoRig.reach, arm.distance + OctoRig.flySpeed * Numeric.clamp(elapsed, 0, 60));
        let distance = arm.distance;
        while (distance < ahead) {
            const next = Math.min(ahead, distance + 1);
            if (this._blockedAt(player, Vec.add(player.position, Vec.scale(direction, next)))) break;
            distance = next;
        }
        return Vec.add(from, Vec.scale(direction, distance));
    }

    /** whether a claw at `at` would be stopped there: something solid on its player's floor, or someone in its way */
    private _blockedAt(player: Player, at: Vector): boolean {
        const circle = new CircleHitbox(OctoRig.clawRadius, at);
        for (const object of Game.objects.getCategory(ObjectCategory.Obstacle)) {
            const definition = object.definition;
            if (!adjacentOrEquivLayer(object, player.layer) || definition.noCollisions || definition.isStair || definition.trap !== undefined) continue;
            if (object.dead && !(definition.isWindow && !definition.noCollisionAfterDestroyed)) continue;
            if (object.hitbox?.collidesWith(circle)) return true;
        }
        for (const object of Game.objects.getCategory(ObjectCategory.Building)) {
            if (adjacentOrEquivLayer(object, player.layer) && object.hitbox?.collidesWith(circle)) return true;
        }
        for (const other of Game.objects.getCategory(ObjectCategory.Player)) {
            if (other === player || other.dead || other.air !== undefined || !adjacentOrEqualLayer(player.layer, other.layer)) continue;
            if (other.hitbox.collidesWith(circle)) return true;
        }
        return false;
    }

    private _sparks(at: Vector, layer: Layer, count: number, tint: number): void {
        for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            ParticleManager.spawnParticle({
                frames: "fire_ember",
                position: Vec.add(at, Vec.fromPolar(angle, 0.3)),
                // (units a second: a spark flies a few units and dies)
                speed: Vec.fromPolar(angle, 9 + Math.random() * 16),
                lifetime: 260 + Math.random() * 220,
                zIndex: ZIndexes.Players + 0.6,
                layer,
                scale: { start: 0.55 + Math.random() * 0.35, end: 0.1 },
                alpha: { start: 1, end: 0 },
                tint,
                emissive: true
            });
        }
    }

    /** every frame: the arms drawn where they are, alive; the pull's dust; the harness with the body */
    update(delta: number): void {
        this._time += delta;
        const now = performance.now();
        for (const [player, rig] of this._rigs) {
            if (player.destroyed || player.dead) {
                this.forget(player);
                continue;
            }
            const shown = player.air === undefined && player.container.visible;
            rig.harness.visible = shown;
            if (player.layer !== rig.layer || rig.arms[0]?.container.parent !== (player.container.parent ?? null)) this._mount(player, rig);
            const data = player.octoArms;
            for (const view of rig.arms) {
                view.container.visible = shown;
                if (!shown) continue;
                // (as faint as the body: a climb fades it)
                view.container.alpha = player.container.alpha;
                this._drawArm(player, rig, view, data?.[view.index] ?? { state: OctoArmState.Ready, angle: 0, distance: 0 }, delta, now);
            }
            if (shown) this._pulled(player, rig, now);
        }
    }

    /** the harness's own frame: the player's drawn position, rotation and size (pixels; a climb's or a landing's moment too) */
    private _frame(player: Player): { readonly at: Vector, readonly rotation: number, readonly size: number } {
        return { at: Vec(player.container.position.x, player.container.position.y), rotation: player.container.rotation, size: player.container.scale.x || player.sizeMod };
    }

    /** a point of the player's own frame (units) in the world (pixels) */
    private _local(frame: { readonly at: Vector, readonly rotation: number, readonly size: number }, point: Vector): Vector {
        const c = Math.cos(frame.rotation), s = Math.sin(frame.rotation);
        const k = PIXI_SCALE * frame.size;
        return Vec(frame.at.x + (point.x * c - point.y * s) * k, frame.at.y + (point.x * s + point.y * c) * k);
    }

    private _drawArm(player: Player, rig: RigView, view: ArmView, arm: OctoArmData, delta: number, now: number): void {
        const frame = this._frame(player);
        const mountDef = OctoRig.mounts[view.index] ?? OctoRig.mounts[0];
        const mount = this._local(frame, Vec(-mountDef.back, mountDef.side));
        const pose = POSES[view.index] ?? POSES[0];
        const t = this._time / 1000 + view.phase;
        const ease = 1 - Math.exp(-delta / FOLLOW_MS[view.state]);
        const resting = view.state === OctoArmState.Ready;
        // (a change of state: the claw goes on from where it's drawn, whichever frame it's reckoned in)
        if (resting !== view.wasResting) {
            if (resting) view.localTip = this._toLocal(frame, view.tip);
            else view.tip = this._local(frame, view.localTip);
            view.wasResting = resting;
        }

        // where the claw's wrist should be, the arm's bends on the way there, and the claw's own turn and spread
        let wrist: Vector;
        let bend1: Vector;
        let bend2: Vector;
        let clawTurn = 0;
        let clawOpen = 1;
        if (resting) {
            // alive: the resting pose, swaying on its own slow clock, the claw looking about; dragged back a little
            // while its player is pulled along another arm
            const sway = Vec(Math.sin(t * 0.9) * 0.5 + Math.sin(t * 2.3) * 0.14, Math.cos(t * 0.7) * 0.45 + Math.sin(t * 1.9) * 0.12);
            const lift = 0.92 + 0.08 * Math.sin(t * 1.3);
            const drag = this._dragOf(player, rig, frame);
            const localTarget = Vec(pose.tip.x * lift + sway.x + drag.x, pose.tip.y * lift + sway.y + drag.y);
            view.localTip = Vec.add(view.localTip, Vec.scale(Vec.sub(localTarget, view.localTip), ease));
            wrist = this._local(frame, view.localTip);
            bend1 = this._local(frame, Vec(pose.bend1.x + sway.x * 0.3 + drag.x * 0.4, pose.bend1.y + sway.y * 0.3 + drag.y * 0.4));
            bend2 = this._local(frame, Vec(pose.bend2.x + sway.x * 0.7 + drag.x * 0.8, pose.bend2.y + sway.y * 0.6 + drag.y * 0.8));
            clawTurn = pose.turn + 0.35 * Math.sin(t * 0.8) + 0.12 * Math.sin(t * 3.1);
            clawOpen = 1 + 0.06 * Math.sin(t * 1.6);
            view.tip = wrist;
        } else {
            const claw = view.state === OctoArmState.Pulling && view.hold
                ? view.hold
                : view.state === OctoArmState.Flying
                    ? toPixiCoords(this._flyingClaw(player, arm, now - view.dataAt))
                    : toPixiCoords(this._clawOf(player, arm));
            // (the claw's prongs are at the point the server says: its wrist a claw's length short of it)
            const toClaw = Vec.sub(claw, mount);
            const reach = Math.max(1, Vec.len(toClaw));
            const dir = Vec.scale(toClaw, 1 / reach);
            const target = Vec.sub(claw, Vec.scale(dir, Math.min(reach * 0.5, CLAW_REACH * frame.size)));
            view.tip = Vec.add(view.tip, Vec.scale(Vec.sub(target, view.tip), ease));
            wrist = view.tip;
            const line = Vec.sub(wrist, mount);
            const length = Math.max(1, Vec.len(line));
            const along = Vec.scale(line, 1 / length);
            const side = Vec(-along.y, along.x);
            const age = now - view.changedAt;
            if (view.state === OctoArmState.Flying) {
                // whipping out: a shiver that dies as it straightens
                const shiver = Math.sin(age / 28) * Math.exp(-age / 260) * Math.min(26, length * 0.08);
                bend1 = Vec.add(Vec.add(mount, Vec.scale(along, length * 0.3)), Vec.scale(side, shiver));
                bend2 = Vec.add(Vec.add(mount, Vec.scale(along, length * 0.72)), Vec.scale(side, -shiver * 0.6));
                clawOpen = 1.14;
            } else if (view.state === OctoArmState.Pulling) {
                // taut, shivering under the load
                const shiver = Math.sin(age / 17) * Math.min(5, length * 0.012);
                bend1 = Vec.add(Vec.add(mount, Vec.scale(along, length * 0.33)), Vec.scale(side, shiver));
                bend2 = Vec.add(Vec.add(mount, Vec.scale(along, length * 0.66)), Vec.scale(side, -shiver));
                clawOpen = 0.86;
            } else {
                // coming back slack: a wave rolling down the line
                const wave = Math.sin(age / 190) * Math.min(34, length * 0.16);
                bend1 = Vec.add(Vec.add(mount, Vec.scale(along, length * 0.3)), Vec.scale(side, wave));
                bend2 = Vec.add(Vec.add(mount, Vec.scale(along, length * 0.7)), Vec.scale(side, -wave * 0.7));
                clawOpen = 1.05;
            }
        }

        // the links strung along the curve from the harness to the wrist, one every LINK_SPACING pixels from the wrist
        // (so they keep their places on the chain as it lengthens, paid out from under the body at the harness, and
        // reeled in there: never slid along it)
        const steps = 48;
        const points: Vector[] = [];
        const along: number[] = [0];
        let total = 0;
        for (let i = 0; i <= steps; i++) {
            const p = cubic(mount, bend1, bend2, wrist, i / steps);
            if (i > 0) total += Vec.len(Vec.sub(p, points[i - 1]));
            points.push(p);
            if (i > 0) along.push(total);
        }
        const spacing = Math.max(LINK_SPACING * frame.size, total / (MAX_LINKS - 1));
        const count = Math.min(MAX_LINKS, Math.max(2, Math.ceil(total / spacing) + 1));
        while (view.links.length < count) {
            const link = new TrenchioSprite("octo_segment");
            view.container.addChildAt(link, 0);
            view.links.push(link);
        }
        let seg = 0;
        for (let i = 0; i < view.links.length; i++) {
            const link = view.links[i];
            if (i >= count) {
                link.visible = false;
                continue;
            }
            const d = Math.max(0, total - (count - 1 - i) * spacing - spacing * 0.5);
            while (seg < steps - 1 && along[seg + 1] < d) seg++;
            const span = along[seg + 1] - along[seg] || 1;
            const u = Numeric.clamp((d - along[seg]) / span, 0, 1);
            const p = Vec.lerp(points[seg], points[seg + 1], u);
            const q = points[Math.min(steps, seg + 1)];
            const tangent = Math.atan2(q.y - points[seg].y, q.x - points[seg].x);
            link.visible = true;
            link.position.set(p.x, p.y);
            link.rotation = tangent;
            // tapering toward the claw, the size of its player
            const taper = LINK_SCALE * (1 - 0.2 * Numeric.clamp(d / Math.max(1, total), 0, 1)) * frame.size;
            link.scale.set(taper, taper);
        }
        // the claw at the wrist, turned along the last of the curve, opened or closed as it's doing
        const before = points[steps - 2];
        const heading = Math.atan2(wrist.y - before.y, wrist.x - before.x) + clawTurn;
        view.claw.position.set(wrist.x, wrist.y);
        view.claw.rotation = heading;
        view.claw.scale.set(CLAW_SCALE * frame.size, CLAW_SCALE * frame.size * clawOpen);
        // the lens: a glow that pulses, bright while it flies or holds
        const lens = Vec.add(wrist, Vec.fromPolar(heading, LENS * CLAW_SCALE * frame.size));
        view.glow.position.set(lens.x, lens.y);
        const busy = !resting;
        const pulse = 0.5 + 0.5 * Math.sin(t * (busy ? 9 : 2.2));
        // (a breath of light at rest, bright while it works)
        view.glow.alpha = busy ? 0.55 + 0.35 * pulse : 0.16 + 0.16 * pulse;
        const glowSize = GLOW * frame.size * (busy ? 1.2 + 0.2 * pulse : 0.9 + 0.1 * pulse);
        view.glow.width = view.glow.height = glowSize;
    }

    /** a resting arm dragged back a little (the player's own units) while its player is pulled along another */
    private _dragOf(player: Player, rig: RigView, frame: { readonly at: Vector, readonly rotation: number, readonly size: number }): Vector {
        const pulling = rig.arms.find(arm => arm.state === OctoArmState.Pulling);
        if (!pulling?.hold) return Vec(0, 0);
        const line = Vec.sub(pulling.hold, frame.at);
        const length = Vec.len(line);
        if (length < 1) return Vec(0, 0);
        // (the pull's direction in the player's own frame, the arms streaming the other way)
        const c = Math.cos(-frame.rotation), s = Math.sin(-frame.rotation);
        const local = Vec((line.x * c - line.y * s) / length, (line.x * s + line.y * c) / length);
        return Vec.scale(local, -1.4);
    }

    /** a point of the world (pixels) in the player's own frame (units) */
    private _toLocal(frame: { readonly at: Vector, readonly rotation: number, readonly size: number }, point: Vector): Vector {
        const c = Math.cos(-frame.rotation), s = Math.sin(-frame.rotation);
        const k = PIXI_SCALE * frame.size;
        const dx = point.x - frame.at.x, dy = point.y - frame.at.y;
        return Vec((dx * c - dy * s) / k, (dx * s + dy * c) / k);
    }

    /** a player dragged along an arm: dust kicked up behind their boots (the winch is heard from `_place`) */
    private _pulled(player: Player, rig: RigView, now: number): void {
        const pulling = rig.arms.find(arm => arm.state === OctoArmState.Pulling);
        if (!pulling?.hold) return;
        const from = Vec(player.container.position.x, player.container.position.y);
        const line = Vec.sub(pulling.hold, from);
        const length = Vec.len(line);
        if (length < 1) return;
        const dir = Vec.scale(line, 1 / length);
        if (now - rig.dustAt > 55) {
            rig.dustAt = now;
            // (behind the boots as they're drawn; the puffs drift back and out, units a second)
            const behind = Vec.add(player.drawnPosition, Vec.scale(dir, -1.6));
            for (let i = 0; i < 2; i++) {
                const spread = (Math.random() - 0.5) * 1.6;
                ParticleManager.spawnParticle({
                    frames: "smoke_particle",
                    position: Vec.add(behind, Vec.scale(Vec(-dir.y, dir.x), spread)),
                    speed: Vec.add(Vec.scale(dir, -4.5), Vec.scale(Vec(-dir.y, dir.x), spread * 3)),
                    lifetime: 420 + Math.random() * 200,
                    zIndex: ZIndexes.Players - 0.5,
                    layer: player.layer,
                    scale: { start: 0.28 + Math.random() * 0.12, end: 0.75 },
                    alpha: { start: 0.42, end: 0 },
                    tint: 0xb09870
                });
            }
        }
    }
}

export const OctoFx = new OctoFxClass();
